// lib/slides/stampLayout.ts
//
// ONE layout for the URW "DESIGN CONCEPT & 3M ZONE APPROVAL" validation card.
//
// The card is drawn twice: as HTML on the editor canvas (SlidesValidationCard)
// and into the PDF at export (app/api/slides/export/route.ts, drawStampPage).
// Those two used to carry their own copy of the geometry, and they drifted: the
// PDF grew its rows to fit each comment while the editor clipped them, so the
// reviewer read "Wide open entrance" on screen and "Wide open entrance ensures
// high transparency (>75%)." in the deliverable. Both now call this module, so
// there is a single definition of every number.
//
// Both renderers use pdf-lib's standard Helvetica metrics in page coordinates.
// The browser paints the resulting lines rather than reflowing them at each zoom.
//
// The one sizing rule: rows grow to fit their text, and the base font shrinks
// until the whole card fits its box. Text is never truncated to make it fit — a
// stamp that cuts the reviewer's verdict mid-sentence is worse than a small one.

import type { DeckReviewState } from '#types/types';
import { SLIDES_VALIDATION_POINTS, resolveAuthorityStatus, stampPointLabel, stampStrings } from './validationPoints';
import { measureStandardText, standardDisplayText, standardFontFor } from './standardText';

export const measureStampText: StampMeasureText = (text, size, bold) =>
  measureStandardText(text, size, standardFontFor('sans', bold));

// Preserve reviewer paragraph breaks while sharing the PDF's supported glyphs.
export const stampDisplayText = (text: string): string =>
  standardDisplayText(text);

/** Width of `text` at `size` in the card's font. `bold` selects the bold face. */
export type StampMeasureText = (text: string, size: number, bold: boolean) => number;

interface StampRowInput {
  label: string;
  comment?: string;
  bold?: boolean;
}

export interface StampLayoutInput {
  /** Card width, in the renderer's own units (PDF points or CSS pixels). */
  width: number;
  /** Box height the card must fit into. Unbounded when omitted. */
  maxHeight?: number;
  /** The reviewer's text-size preference; the fit still caps it. */
  fontScale?: number;
  /** The 10 guideline rows, in order. */
  points: StampRowInput[];
  /** Absent on the version of the card without the authorities line. */
  authorities?: StampRowInput;
  statusRow: StampRowInput;
  footer: string;
  title?: string;
  metadata?: Array<Array<{
    key: 'rdm' | 'cellule' | 'centre' | 'date' | 'issue';
    label: string;
    value: string;
    labelX: number;
    valueX: number;
    endX: number;
  }>>;
  measure: StampMeasureText;
}

export interface StampRowLayout {
  h: number;
  labelSize: number;
  labelLines: string[];
  commentLines: string[];
}

export interface StampLayout {
  fs: number;
  /** Font size of the big row number (1..10). */
  numFs: number;
  numW: number;
  labelW: number;
  checkW: number;
  /** Left inset of the comment column inside its cell. */
  commentPad: number;
  commentW: number;
  headerH: number;
  logos: {
    urw: { x: number; y: number; width: number; height: number };
    cs: { x: number; y: number; width: number; height: number };
  };
  infoRows: Array<{
    h: number;
    fields: Array<{
      key: 'rdm' | 'cellule' | 'centre' | 'date' | 'issue';
      label: string;
      value: string;
      labelX: number;
      valueX: number;
      width: number;
      labelLines: string[];
      valueLines: string[];
    }>;
  }>;
  titleX: number;
  titleLines: string[];
  barH: number;
  points: StampRowLayout[];
  authorities: StampRowLayout | null;
  statusRow: StampRowLayout;
  footerLines: string[];
  footerH: number;
  /** Natural height of the whole card at `fs`. */
  height: number;
  overflow: boolean;
}

/** Base font as a fraction of the card width, before the reviewer's fontScale. */
const BASE_FS_RATIO = 0.022;
/** Absolute font floor. Only a backstop: the card is unreadable long before
 *  this, but overflowing the reviewer's box is worse than being small, so the
 *  fit is allowed to go all the way down rather than give up and spill. */
export const STAMP_MIN_FS = 1;
// Keep recoverable overflow previews finite without cutting ordinary long comments.
export const layoutLineLimit = (height: number, lineHeight: number): number => {
  const count = Math.ceil(height / lineHeight) + 1;
  return Math.min(4096, Math.max(1024, Number.isNaN(count) ? 1024 : count));
};
export const STAMP_OVERFLOW_MESSAGE = 'The validation card does not fit. Enlarge its frame or shorten its comments before exporting.';

export const STAMP_LINE_HEIGHT = 1.15;
export const STAMP_FOOTER_FS_SCALE = 0.74;
export const STAMP_FOOTER_LINE_HEIGHT = 0.95;

/** Greedy word wrap, shared so the PDF and the DOM break lines the same way. */
export function wrapStampText(
  text: string,
  size: number,
  bold: boolean,
  maxWidth: number,
  measure: StampMeasureText,
  maxLines: number = Number.POSITIVE_INFINITY,
): string[] {
  const lines: string[] = [];
  for (const paragraph of text.split(/\r\n?|\n/)) {
    let cur = '';
    for (const word of paragraph.split(/\s+/).filter(Boolean)) {
      const candidate = cur ? `${cur} ${word}` : word;
      if (measure(candidate, size, bold) <= maxWidth) {
        cur = candidate;
        continue;
      }
      if (cur) lines.push(cur);
      if (lines.length >= maxLines) return lines.slice(0, maxLines);
      if (measure(word, size, bold) <= maxWidth) {
        cur = word;
        continue;
      }
      cur = '';
      let advance = 0;
      const glyphWidths = new Map<string, number>();
      for (const character of word) {
        let glyph = glyphWidths.get(character);
        if (glyph === undefined) { glyph = measure(character, 1, bold); glyphWidths.set(character, glyph); }
        if (cur && (advance + glyph) * size > maxWidth) {
          lines.push(cur);
          if (lines.length >= maxLines) return lines.slice(0, maxLines);
          cur = '';
          advance = 0;
        }
        cur += character;
        advance += glyph;
      }
    }
    lines.push(cur);
    if (lines.length >= maxLines) return lines.slice(0, maxLines);
  }
  return lines.slice(0, maxLines);
}

export function buildStampLayout(input: StampLayoutInput): StampLayout {
  const { width: W, maxHeight, points, authorities, statusRow, footer, measure } = input;
  const fontScale = input.fontScale ?? 1;

  const build = (fs: number): StampLayout => {
    const lineLimit = layoutLineLimit(maxHeight ?? Infinity, fs * STAMP_FOOTER_FS_SCALE * STAMP_FOOTER_LINE_HEIGHT);
    let overflow = false;
    const wrap = (text: string, size: number, bold: boolean, width: number, metric = measure) => {
      const lines = wrapStampText(text, size, bold, width, metric, lineLimit);
      overflow ||= lines.length >= lineLimit;
      return lines;
    };
    const numW = W * 0.075;
    const labelW = W * 0.415;
    const checkW = W * 0.078;
    const commentPad = W * 0.012;
    const commentW = W - numW - labelW - checkW - W * 0.024;

    const row = (r: StampRowInput, minH: number, summary = false): StampRowLayout => {
      const labelSize = fs;
      const labelLines = wrap(r.label, labelSize, summary, labelW + (summary ? numW : 0) - fs);
      const commentLines = r.comment
        ? wrap(r.comment, fs, !!r.bold, commentW)
        : [];
      const h = Math.max(
        minH,
        labelLines.length * labelSize * STAMP_LINE_HEIGHT + fs * 0.9,
        commentLines.length * fs * STAMP_LINE_HEIGHT + fs * 0.9,
      );
      return { h, labelSize, labelLines, commentLines };
    };

    const pointRows = points.map((p) => row(p, fs * 2.7));
    const authRow = authorities ? row(authorities, fs * 3, true) : null;
    const statRow = row(statusRow, fs * 3, true);

    const logoBandH = fs * 5.4;
    const infoH = fs * 2.1;
    const infoRows = input.metadata?.map((fields) => {
      const fitted = fields.map((field) => ({
        key: field.key,
        label: field.label,
        value: field.value,
        labelX: field.labelX * W,
        valueX: field.valueX * W,
        width: (field.endX - field.valueX) * W,
        labelLines: wrap(field.label, fs * 1.05, true, (field.valueX - field.labelX) * W - fs * 0.3),
        valueLines: wrap(field.value, fs * 1.05, false, (field.endX - field.valueX) * W),
      }));
      return {
        fields: fitted,
        h: Math.max(infoH, ...fitted.map((field) => Math.max(field.labelLines.length, field.valueLines.length) * fs * 1.05 * STAMP_LINE_HEIGHT + fs * 0.6)),
      };
    }) ?? [];
    // These are the intrinsic ratios of the two bundled stamp logos.
    const urw = { x: W * 0.02, y: 0, width: logoBandH * 0.62 * (248 / 74), height: logoBandH * 0.62 };
    const cs = { x: urw.x + urw.width + W * 0.03, y: 0, width: logoBandH * 0.4 * (102 / 50), height: logoBandH * 0.4 };
    const logoRight = cs.x + cs.width;
    const titleX = Math.max(W * 0.42, logoRight + W * 0.03);
    const titleLines = input.title
      ? wrap(input.title, fs * 1.35, true, W - titleX - W * 0.02)
      : [];
    const headerH = Math.max(logoBandH, titleLines.length * fs * 1.5 + fs);
    urw.y = (headerH - urw.height) / 2;
    cs.y = (headerH - cs.height) / 2;
    const barH = fs * 2;
    const footerLines = wrap(footer, fs * STAMP_FOOTER_FS_SCALE, false, W - fs * 1.5,
      (text, size) => measureStandardText(text, size, standardFontFor('sans', false, true)));
    const footerH = footerLines.length * fs * STAMP_FOOTER_LINE_HEIGHT + fs * 0.8;
    const height =
      headerH +
      (infoRows.length ? infoRows.reduce((sum, row) => sum + row.h, 0) : infoH * 2) +
      barH +
      pointRows.reduce((a, r) => a + r.h, 0) +
      (authRow?.h ?? 0) +
      statRow.h +
      footerH;

    return {
      fs,
      numFs: fs * 1.1,
      numW,
      labelW,
      checkW,
      commentPad,
      commentW,
      headerH,
      logos: { urw, cs },
      infoRows,
      titleX,
      titleLines,
      barH,
      points: pointRows,
      authorities: authRow,
      statusRow: statRow,
      footerLines,
      footerH,
      height,
      overflow,
    };
  };

  // FIT: the largest font, up to the reviewer's requested size, whose layout
  // still fits the box.
  //
  // Binary search, not a proportional correction. Wrapping is discrete — height
  // is a step function of the font size — so scaling the font by
  // (available / measured) is only a guess, and it is a wild one when many rows
  // sit near a line boundary: on a 10-row review with long comments the guess
  // undershot to the font floor and rendered the card at 42% of the frame the
  // reviewer had sized, unreadable, with a large empty gap below it. Bisection
  // has no such failure mode and converges to ~0.1% in 12 steps.
  const availH = maxHeight ?? Number.POSITIVE_INFINITY;
  const requested = Math.max(STAMP_MIN_FS, W * BASE_FS_RATIO * fontScale);
  let best = build(requested);
  if (best.height <= availH && !best.overflow) return best;

  let lo = STAMP_MIN_FS;
  let hi = requested;
  best = build(lo);
  if (best.height > availH || best.overflow) return best;
  for (let step = 0; step < 12 && hi - lo > 0.01; step += 1) {
    const mid = (lo + hi) / 2;
    const candidate = build(mid);
    if (candidate.height <= availH && !candidate.overflow) {
      best = candidate;
      lo = mid;
    } else {
      hi = mid;
    }
  }
  // `best` is the floor layout when even STAMP_MIN_FS overflows — a box far too
  // small for a 12-row card. Returning it (rather than the requested size) keeps
  // the overflow as small as it can be.
  return best;
}

/** Both renderers fit in page points; the canvas applies zoom after layout. */
export function buildReviewStampLayout(
  review: DeckReviewState,
  width: number,
  maxHeight?: number,
  fontScale = review.stamp?.fontScale ?? 1,
  round = review.round ?? 1,
  deckName = '',
): StampLayout {
  const strings = stampStrings(review.language);
  const status = review.status ?? 'C';
  const header = review.header ?? {};
  const field = (key: 'rdm' | 'cellule' | 'centre' | 'date' | 'issue', label: string, fallback: string, labelX: number, valueX: number, endX: number) => ({
    key, label: `${label} :`, value: stampDisplayText(header[key] ?? fallback), labelX, valueX, endX,
  });
  return buildStampLayout({
    width,
    maxHeight,
    fontScale,
    title: stampDisplayText(strings.title),
    metadata: [
      [field('rdm', strings.meta.rdm, '-', 0.02, 0.12, 0.29), field('cellule', strings.meta.unit, deckName || '-', 0.30, 0.40, 0.63), field('date', strings.meta.date, '-', 0.64, 0.74, 0.98)],
      [field('centre', strings.meta.centre, '-', 0.02, 0.14, 0.63), field('issue', strings.meta.review, String(round), 0.64, 0.74, 0.98)],
    ],
    points: SLIDES_VALIDATION_POINTS.map((point) => ({ label: stampDisplayText(stampPointLabel(point, review.language)), comment: stampDisplayText(review.points?.[point.id]?.comment ?? '') })),
    // Thibaut: the sentence follows the colour, nothing is ever added to it (2026-09-30), yet every
    // cell stays editable (2026-10-05). A hand rewording replaces it until the colour changes.
    authorities: review.authorityRow === false ? undefined : { label: stampDisplayText(strings.authoritiesRow), comment: stampDisplayText(review.authorityText ?? strings.authorities[resolveAuthorityStatus(review.authorityStatus, status)]) },
    statusRow: { label: stampDisplayText(strings.statusRow), comment: stampDisplayText(review.statusText ?? strings.status[status]), bold: true },
    footer: stampDisplayText(strings.legalFooter),
    measure: measureStampText,
  });
}
